import { CONFERENCE_JOINED, CONFERENCE_LEFT, SET_ROOM } from '../base/conference/actionTypes';
import { hangup } from '../base/connection/actions.web';
// @ts-ignore - internal lib entry, no type declarations.
import JitsiMeetJS from '../base/lib-jitsi-meet/_';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { TRACK_ADDED, TRACK_REMOVED } from '../base/tracks/actionTypes';
import { getLocalJitsiAudioTrack } from '../base/tracks/functions.web';
import { setSessionTimeoutConfig, setSessionTimeoutRemaining, setSessionTimeoutWarning } from './actions';
import logger from './logger';
/**
 * これ以上の音量なら「喋っている」とみなす。0..1。
 *
 * 呼吸や空調で振り切らない程度に上げてある。ここを 0 にすると常時「操作あり」に
 * なってタイマーが一生走らない。逆に高すぎると、小声の人が無操作扱いで落ちる。
 */
const SPEAK_LEVEL = 0.02;
const JitsiTrackEvents = JitsiMeetJS.events.track;
/** 残り時間の計算と表示の更新の間隔。 */
const TICK_MS = 1000;
/**
 * 予告を出す残り秒数の既定。config.js の deepaiSessionTimeout.warnSeconds で変えられる。
 */
const DEFAULT_WARN_SECONDS = 60;
/*
 * 時刻はモジュール変数に置く。
 *
 * 音量イベントは毎秒何度も飛んでくるので、そのたびに redux へ action を撒くと
 * 会議中ずっと無駄な再描画が走る。redux に出すのは1秒ごとの残り時間だけにする。
 */
let _joinedAt = 0;
let _lastActionAt = 0;
let _tickTimer = null;
let _hangingUp = false;
/** 画面操作の購読を外すための後始末。 */
let _detachDom = null;
/** 音量の購読を外すための後始末。 */
let _detachAudio = null;
/**
 * 「いま操作があった」と記録する。
 *
 * @returns {void}
 */
function _markAction() {
    _lastActionAt = Date.now();
}
/**
 * 画面操作を拾う。
 *
 * mousemove は使わない。カーソルが少し震えただけで延長されるのは
 * 「無操作」の定義として緩すぎる。押した・叩いた・触れた、だけを数える。
 *
 * @returns {void}
 */
function _attachDom() {
    if (_detachDom || typeof document === 'undefined') {
        return;
    }
    const events = ['pointerdown', 'keydown', 'touchstart'];
    const opts = { capture: true,
        passive: true };
    for (const e of events) {
        document.addEventListener(e, _markAction, opts);
    }
    _detachDom = () => {
        for (const e of events) {
            document.removeEventListener(e, _markAction, opts);
        }
        _detachDom = null;
    };
}
/**
 * 自分のマイクの音量を購読する。
 *
 * ★これが主役。この製品では画面に触らず喋り続けるのが普通の使い方なので、
 *   発話を数えないと訓練の最中に退出させてしまう。
 *
 * ★相手(AI)の発話は数えない。AI が話し続けると一生切れなくなる。
 *
 * @param {Object} store - The redux store.
 * @returns {void}
 */
function _attachAudio(store) {
    _detachAudio?.();
    const track = getLocalJitsiAudioTrack(store.getState());
    if (!track) {
        return;
    }
    const onLevel = (level) => {
        if (level > SPEAK_LEVEL) {
            _markAction();
        }
    };
    track.on(JitsiTrackEvents.TRACK_AUDIO_LEVEL_CHANGED, onLevel);
    _detachAudio = () => {
        try {
            track.off(JitsiTrackEvents.TRACK_AUDIO_LEVEL_CHANGED, onLevel);
        }
        catch (e) {
            logger.warn('session-timeout: 音量の購読解除に失敗', e);
        }
        _detachAudio = null;
    };
}
/**
 * 監視をすべて止める。退出時と、設定が無いときに呼ぶ。
 *
 * @returns {void}
 */
function _stop() {
    if (_tickTimer !== null) {
        clearInterval(_tickTimer);
        _tickTimer = null;
    }
    _detachDom?.();
    _detachAudio?.();
    _joinedAt = 0;
    _hangingUp = false;
}
/**
 * 1秒ごとの判定。
 *
 * setTimeout で「N分後に切る」と予約しない。タブが背面にあるとブラウザが
 * タイマーを丸めるので、予約は当てにならない。毎回いまの時刻から引き算する。
 *
 * @param {Object} store - The redux store.
 * @returns {void}
 */
function _tick(store) {
    const state = store.getState();
    const { config } = state['features/session-timeout'];
    if (!config.enabled || !_joinedAt || _hangingUp) {
        return;
    }
    const now = Date.now();
    const idleLeftMs = (_lastActionAt + (config.idleMinutes * 60000)) - now;
    const maxLeftMs = (_joinedAt + (config.maxMinutes * 60000)) - now;
    store.dispatch(setSessionTimeoutRemaining(idleLeftMs, maxLeftMs));
    if (idleLeftMs <= 0 || maxLeftMs <= 0) {
        _hangingUp = true;
        const why = maxLeftMs <= 0 ? '利用時間の上限' : '操作がないため';
        logger.info(`session-timeout: ${why}退出します`);
        store.dispatch(setSessionTimeoutWarning(null));
        // 理由を渡すと、会議終了の画面に文言として出る。予告なく画面が
        // 変わると、利用者は不具合と区別できない。
        store.dispatch(hangup(false, `${why}退出しました。`, true));
        return;
    }
    // 予告。短いほうのタイマーで出す。max のほうが先なら延長させない。
    const warnMs = _warnSeconds(state) * 1000;
    const next = maxLeftMs <= idleLeftMs ? 'max' : 'idle';
    const leftMs = Math.min(idleLeftMs, maxLeftMs);
    const shown = state['features/session-timeout'].warning;
    if (leftMs <= warnMs && shown !== next) {
        store.dispatch(setSessionTimeoutWarning(next));
    }
    else if (leftMs > warnMs && shown !== null) {
        // 喋る/操作すると idle が伸びるので、予告は自動で引っ込む。
        store.dispatch(setSessionTimeoutWarning(null));
    }
}
/**
 * 予告を出す残り秒数。
 *
 * @param {Object} state - The redux state.
 * @returns {number}
 */
function _warnSeconds(state) {
    const n = state['features/base/config']?.deepaiSessionTimeout?.warnSeconds;
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_WARN_SECONDS;
}
/**
 * 部屋のタイムアウト設定を取りに行く。
 *
 * ★取れなければ「切らない」。404（未設定）も、CORS 拒否も、通信断も同じ扱いにする。
 *   どれも「切ってよいと確認できていない」であり、そこで人を退出させてはいけない。
 *
 * ★待機画面のパネル(PrejoinPanel)には相乗りさせない。あちらは業務が
 *   割り当てられていない部屋では丸ごと出ないので、業務の無い部屋だけ
 *   タイムアウトが効かなくなる。
 *
 * @param {Object} store - The redux store.
 * @param {string} room - 部屋名。
 * @returns {Promise<void>}
 */
async function _fetchConfig(store, room) {
    const state = store.getState();
    const baseUrl = state['features/base/config'].deepai_web_server_url ?? '';
    if (!room || !baseUrl) {
        return;
    }
    const origin = baseUrl.replace(/\/+$/, '');
    const url = `${origin}/api/meeting-ops/rooms/${encodeURIComponent(room)}/timeout`;
    try {
        const res = await fetch(url);
        if (!res.ok) {
            logger.info(`session-timeout: 設定を取得できないので切りません（${res.status}）`);
            return;
        }
        const d = await res.json();
        if (!d?.enabled) {
            logger.info('session-timeout: この部屋は設定なし。切りません');
            return;
        }
        const idleMinutes = Number(d.idle_minutes);
        const maxMinutes = Number(d.max_minutes);
        if (!(idleMinutes > 0) || !(maxMinutes > 0)) {
            logger.warn('session-timeout: 設定の値がおかしいので切りません', d);
            return;
        }
        logger.info(`session-timeout: 無操作${idleMinutes}分 / 上限${maxMinutes}分`);
        store.dispatch(setSessionTimeoutConfig({ enabled: true,
            idleMinutes,
            maxMinutes }));
    }
    catch (e) {
        logger.info('session-timeout: 設定を取得できないので切りません', e);
    }
}
MiddlewareRegistry.register(store => next => action => {
    const result = next(action);
    switch (action.type) {
        case SET_ROOM:
            // 部屋名が決まった時点で取りに行く。入室より前なので、入室した瞬間から効く。
            _fetchConfig(store, action.room);
            break;
        case CONFERENCE_JOINED: {
            const { config } = store.getState()['features/session-timeout'];
            if (!config.enabled) {
                break;
            }
            _joinedAt = Date.now();
            _markAction();
            _attachDom();
            _attachAudio(store);
            if (_tickTimer === null && typeof window !== 'undefined') {
                _tickTimer = window.setInterval(() => _tick(store), TICK_MS);
            }
            break;
        }
        case TRACK_ADDED:
        case TRACK_REMOVED:
            // マイクを切り替えるとトラックが作り直される。古いトラックを購読したままだと
            // 音量が来なくなり、喋っていても無操作と判定されて落ちる。
            if (_joinedAt && action.track?.local && action.track?.mediaType === 'audio') {
                _attachAudio(store);
            }
            break;
        case CONFERENCE_LEFT:
            _stop();
            break;
    }
    return result;
});
